import classNames from "classnames";
import { SupportModel } from "client/Api/Support/support";
import { ensureNotNil, isNilOrWhiteSpace } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import {
	PropsWithChildren,
	ReactElement
} from "react";
import { SupportInformation } from "./SupportInformation";
import {
	Anchor,
	ExpandableTitlebar,
	FormattedText,
	HeightAnimationContainer,
	RawHtml
} from "client/Components";
import styles from "./Page.module.less";
import { createRoot } from "react-dom/client";
import { useToggle } from "client/Hooks";
import { HelpfulResourceCard } from "../WelcomeWizard/HelpfulResourceCard";
import { helpfulResourceCardConfiguration } from "../WelcomeWizard/CompleteStep";

type PageProps = {
	model: SupportModel;
};

const Page = (props: PageProps): ReactElement => {
	const {
		model
	} = props;

	return (
		<div className={styles.root}>
			<ExpandableSection
				title={localize("_SupportInformation")}
				isInitiallyExpanded
			>
				<div
					className={classNames(
						styles.expandableSectionTextualContent,
						styles.expandableSectionTextualContent_withUnderlinedLinks
					)}
				>
					{!isNilOrWhiteSpace(model.customSupportPageHtml)
						? (
							<RawHtml html={model.customSupportPageHtml} />
						)
						: (
							<>
								<h4>{localize("_SupportText1")}</h4>
								<span className={styles.supportSubText}>
									<FormattedText
										text={localize("_SupportText2")}
										thingsToWrap={[
											{
												blockId: "Url",
												wrap: (x) => <Anchor href={model.siteLink} target="_blank">{x}</Anchor>
											}
										]}
									/>
								</span>
							</>
						)
					}
				</div>
			</ExpandableSection>
			<ExpandableSection
				title={localize("_HelpFulGuides")}
			>
				<ul className={styles.cards}>
					{helpfulResourceCardConfiguration.map((x, i): ReactElement => (
						<li key={i}>
							<HelpfulResourceCard
								configuration={x}
							/>
						</li>
					))}
				</ul>
			</ExpandableSection>
			<ExpandableSection
				title={localize("_TechnicalInformation")}
			>
				<div className={styles.expandableSectionTextualContent}>
					<SupportInformation model={model.supportInformation} />
				</div>
			</ExpandableSection>
		</div>
	);
};
Page.displayName = "Page";

export function init(model: SupportModel): void {
	createRoot(
		ensureNotNil(
			document.getElementById("main-content")
		)
	)
		.render(
			<Page model={model} />
		);
}

type ExpandableSectionProps = {
	title: string;
	isInitiallyExpanded?: boolean;
};

const ExpandableSection = (props: PropsWithChildren<ExpandableSectionProps>) => {
	const {
		title,
		isInitiallyExpanded = false,
		children
	} = props;

	const [isExpanded, toggleIsExpanded] = useToggle(isInitiallyExpanded);

	return (
		<div>
			<ExpandableTitlebar
				text={title}
				isExpanded={isExpanded}
				onToggle={toggleIsExpanded}
				trackingId="title"
			/>
			<HeightAnimationContainer isVisible={isExpanded}>
				<div className={styles.expandableSectionContent}>
					{children}
				</div>
			</HeightAnimationContainer>
		</div>
	);
};

ExpandableSection.displayName = "ExpandableSection";
